// assets/js/components/revenue-calculator/intl-data.js
//
// Country/currency reference data comes from the browser's Intl API, not a
// hand-typed table. The source PDF's country list was OCR'd from a scanned
// document and several non-Latin currency symbols (Armenian dram, Georgian
// lari, Bengali taka, etc.) came through as unrecognizable glyphs — Intl
// guarantees correct, already-localized names/symbols instead.
//
// The one static table below (ISO 3166-1 alpha-2 region -> ISO 4217 currency
// code) is unavoidable: Intl has no "list all countries" API. It is plain
// ASCII letter codes, not Unicode symbols, so it carries none of the OCR risk.

var REGION_CURRENCY = {
    AF: "AFN", AL: "ALL", DZ: "DZD", AD: "EUR", AO: "AOA", AG: "XCD", AR: "ARS",
    AM: "AMD", AU: "AUD", AT: "EUR", AZ: "AZN", BS: "BSD", BH: "BHD", BD: "BDT",
    BB: "BBD", BY: "BYN", BE: "EUR", BZ: "BZD", BJ: "XOF", BT: "BTN", BO: "BOB",
    BA: "BAM", BW: "BWP", BR: "BRL", BN: "BND", BG: "EUR", BF: "XOF", BI: "BIF",
    KH: "KHR", CM: "XAF", CA: "CAD", CV: "CVE", CF: "XAF", TD: "XAF", CL: "CLP",
    CN: "CNY", CO: "COP", KM: "KMF", CG: "XAF", CR: "CRC", CI: "XOF", HR: "EUR",
    CU: "CUP", CY: "EUR", CZ: "CZK", DK: "DKK", DJ: "DJF", DM: "XCD", DO: "DOP",
    CD: "CDF", EC: "USD", EG: "EGP", SV: "USD", GQ: "XAF", ER: "ERN", EE: "EUR",
    SZ: "SZL", ET: "ETB", FJ: "FJD", FI: "EUR", FR: "EUR", GA: "XAF", GM: "GMD",
    GE: "GEL", DE: "EUR", GH: "GHS", GR: "EUR", GD: "XCD", GT: "GTQ", GN: "GNF",
    GW: "XOF", GY: "GYD", HK: "HKD", HT: "HTG", HN: "HNL", HU: "HUF", IS: "ISK", IN: "INR",
    ID: "IDR", IR: "IRR", IQ: "IQD", IE: "EUR", IL: "ILS", IT: "EUR", JM: "JMD",
    JP: "JPY", JO: "JOD", KZ: "KZT", KE: "KES", KI: "AUD", KW: "KWD", KG: "KGS",
    LA: "LAK", LV: "EUR", LB: "LBP", LS: "LSL", LR: "LRD", LY: "LYD", LI: "CHF",
    LT: "EUR", LU: "EUR", MG: "MGA", MW: "MWK", MY: "MYR", MV: "MVR", ML: "XOF",
    MT: "EUR", MH: "USD", MR: "MRU", MU: "MUR", MX: "MXN", FM: "USD", MD: "MDL",
    MC: "EUR", MN: "MNT", MO: "MOP", ME: "EUR", MA: "MAD", MZ: "MZN", MM: "MMK", NA: "NAD",
    NR: "AUD", NP: "NPR", NL: "EUR", NZ: "NZD", NI: "NIO", NE: "XOF", NG: "NGN",
    KP: "KPW", MK: "MKD", NO: "NOK", OM: "OMR", PK: "PKR", PW: "USD", PA: "PAB",
    PG: "PGK", PY: "PYG", PE: "PEN", PH: "PHP", PL: "PLN", PT: "EUR", QA: "QAR",
    RO: "RON", RU: "RUB", RW: "RWF", KN: "XCD", LC: "XCD", VC: "XCD", WS: "WST",
    SM: "EUR", ST: "STN", SA: "SAR", SN: "XOF", RS: "RSD", SC: "SCR", SL: "SLE",
    SG: "SGD", SK: "EUR", SI: "EUR", SB: "SBD", SO: "SOS", ZA: "ZAR", KR: "KRW",
    SS: "SSP", ES: "EUR", LK: "LKR", SD: "SDG", SR: "SRD", SE: "SEK", CH: "CHF",
    SY: "SYP", TW: "TWD", TJ: "TJS", TZ: "TZS", TH: "THB", TL: "USD", TG: "XOF",
    TO: "TOP", TT: "TTD", TN: "TND", TR: "TRY", TM: "TMT", TV: "AUD", UG: "UGX",
    UA: "UAH", AE: "AED", GB: "GBP", US: "USD", UY: "UYU", UZ: "UZS", VU: "VUV",
    VA: "EUR", VE: "VES", VN: "VND", YE: "YER", ZM: "ZMW", ZW: "ZWG"
};

function getCountries(locale) {
    var dn = new Intl.DisplayNames([locale || "en"], { type: "region" });
    var out = Object.keys(REGION_CURRENCY).map(function (region) {
        return { region: region, name: dn.of(region), currency: REGION_CURRENCY[region] };
    });
    out.sort(function (a, b) { return a.name.localeCompare(b.name); });
    return out;
}

function getCurrencyName(locale, currencyCode) {
    try {
        return new Intl.DisplayNames([locale || "en"], { type: "currency" }).of(currencyCode);
    } catch (e) {
        return currencyCode;
    }
}

function getCurrencySymbol(locale, currencyCode) {
    try {
        var parts = new Intl.NumberFormat(locale || "en", { style: "currency", currency: currencyCode }).formatToParts(1);
        var part = parts.filter(function (p) { return p.type === "currency"; })[0];
        return part ? part.value : currencyCode;
    } catch (e) {
        return currencyCode;
    }
}

// Whole-unit money in the page locale: symbol position, spacing and grouping come from
// Intl ("$4,632", "CHF 4,632", "4 632 €" in fr). Gluing symbol + number by hand gave
// "CHF4,632". Mirrored server-side by ReportViewBuilder::money() so email == page.
function formatMoney(locale, currencyCode, value) {
    var n = Math.max(0, Math.round(Number(value) || 0));
    try {
        return new Intl.NumberFormat(locale || "en", {
            style: "currency", currency: currencyCode || "USD", minimumFractionDigits: 0, maximumFractionDigits: 0
        }).format(n);
    } catch (e) {
        return (currencyCode ? currencyCode + " " : "$") + n.toLocaleString();
    }
}

function getAllCurrencyCodes() {
    var seen = {};
    Object.keys(REGION_CURRENCY).forEach(function (region) { seen[REGION_CURRENCY[region]] = true; });
    return Object.keys(seen).sort();
}

module.exports = { getCountries: getCountries, getCurrencyName: getCurrencyName, getCurrencySymbol: getCurrencySymbol, getAllCurrencyCodes: getAllCurrencyCodes, formatMoney: formatMoney };
